import { useFormatter, useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useEffect } from 'react';

import { Spinner } from '@/components/_atoms/Spinner';
import { Typography } from '@/components/_atoms/Typography';
import { getProductShortName } from '@/logic/product/products.utils';
import { useTyreProductSalesQuery } from '@/network/_queries/tyreProduct.queries';
import { ApiErrorCodes, isApiError } from '@/network/api.utils';
import { schemas } from '@/network/client';

import { ModalCard } from '../ModalCard';
import { useModals } from '../Modals';

type ModalConfirmProps = {
  product: {
    tread: string;
    producer: string;
    sku: string;
    attributes?: {
      size: string;
      loadIndex: string;
      speedIndex: string;
    };
  };
  sold: number;
  close: () => void;
};

export const ProductSoldModal = ({ sold, product, close }: ModalConfirmProps) => {
  const t = useTranslations();
  const format = useFormatter();
  const { openInfoModal } = useModals();
  const router = useRouter();
  const { sku, producer, tread, attributes } = product;
  const { isLoading, data, error } = useTyreProductSalesQuery(sku);

  const _data = data as schemas['ProductSales'][] | undefined;
  useEffect(() => {
    if (isApiError(error) && error.data.error_code === ApiErrorCodes.getProductSales.VARIANT_SOLD_OUT) {
      close();
      openInfoModal({
        title: t('productSoldModal.VARIANT_SOLD_OUT.title'),
        description: t('productSoldModal.VARIANT_SOLD_OUT.description'),
        label: t('productSoldModal.VARIANT_SOLD_OUT.label'),
        onConfirm: () => router.reload(),
      });
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="flex h-20 w-full items-center justify-center">
        <Spinner visible={true} />
      </div>
    );
  }

  return (
    <ModalCard close={close} size="m" className="flex h-[600px] max-h-[calc(100vh-4rem)] flex-col p-0">
      <div className="border-b border-grey-300 p-6 pr-12">
        <Typography variant="body_xxl" bold className="mb-2">
          {t('productSoldModal.title', { sold })}
        </Typography>
        <div className="flex flex-col gap-1">
          <Typography variant="body_m" id="productSoldModal.description" values={{ producer, tread }} />
          {attributes ? <Typography variant="body_s">{getProductShortName(attributes)}</Typography> : null}
        </div>
      </div>
      {_data ? (
        <OverlayScrollbarsComponent className="mr-0.5 p-6">
          <div className="grid grid-cols-2 pb-4">
            <Typography variant="body_l" bold className="text-center">
              {t('productSoldModal.tableUser')}
            </Typography>
            <Typography variant="body_l" bold className="text-center">
              {t('productSoldModal.tableDate')}
            </Typography>
          </div>

          <div className="flex flex-col gap-1">
            {_data?.map((item, index) => (
              <div
                key={item.date}
                className="group grid grid-cols-2 items-center justify-between gap-[1px]"
                data-bg={index % 2 === 1}
              >
                <div className="flex h-10 items-center justify-center rounded-e-sm rounded-s-lg group-data-[bg=false]:bg-grey-200">
                  <Typography variant="body_m" color="grey-standard">
                    {item.user}
                  </Typography>
                </div>
                <div className="flex h-10 items-center justify-center rounded-e-lg rounded-s-sm group-data-[bg=false]:bg-grey-200">
                  <Typography variant="body_m" color="grey-standard">
                    {`${format.dateTime(new Date(item.date), {
                      dateStyle: 'short',
                    })} ${format.dateTime(new Date(item.date), {
                      timeStyle: 'short',
                    })}`}
                  </Typography>
                </div>
              </div>
            )) || null}
          </div>
          {/* </div> */}
        </OverlayScrollbarsComponent>
      ) : null}
    </ModalCard>
  );
};
